import { useMemo } from 'react'

import { Toasts, useModalContext } from '@fourthwall/components'
import { useTagManager } from '@hooks'
import { useCreatorIntegrationsV2Query } from '@queries'

import type { AppStatus } from '../../../Apps.types'
import { useAppConnectEvent } from '../../../hooks/useAppConnectEvent'
import { useCreatorIntegrationsQuery } from '../../../queries/useCreatorIntegrationsQuery'
import type { SettingsFormValues } from '../AppsShipStation.types'
import { ConfirmStoreDisconnectModal } from '../components/ConfirmStoreDisconnectModal/ConfirmStoreDisconnectModal'
import { SyncFulfillmentsModal } from '../components/SyncFulfillmentsModal/SyncFulfillmentsModal'
import { useDeleteShipStationConfigurationMutation } from '../queries/useDeleteShipStationConfigurationMutation'
import { usePushToThirdPartyMutation } from '../queries/usePushToThirdPartyMutation'
import { useShipStationConfigurationQuery } from '../queries/useShipStationConfigurationQuery'
import { useUnfulfilledCountQuery } from '../queries/useUnfulfilledCountQuery'
import { useUpdateShipStationConfigurationMutation } from '../queries/useUpdateShipStationConfigurationMutation'
import { isInitialApiSecret } from '../utils/isInitialApiSecret'
import { useShipStationStores } from './useShipStationStores'

export const useShipStation = () => {
  const { open } = useModalContext()
  const { creatorIntegrationsQuery, invalidateCreatorIntegrationsQuery } =
    useCreatorIntegrationsQuery()
  const { invalidateCreatorIntegrationsV2Query } = useCreatorIntegrationsV2Query()
  const { shipStationConfigurationQuery, invalidateShipStationConfigurationQuery } =
    useShipStationConfigurationQuery(undefined, {
      enabled: creatorIntegrationsQuery.data?.shipstation?.status === 'CONNECTED',
    })
  const {
    values: storeValues,
    stores,
    errorCode: storesErrorCode,
    isLoadingStores,
    setValues: setStoresValues,
    invalidate: invalidateStores,
  } = useShipStationStores(creatorIntegrationsQuery.data?.shipstation?.status)
  const { unfulfilledCountQuery } = useUnfulfilledCountQuery(undefined, {
    enabled: creatorIntegrationsQuery.data?.shipstation?.status === 'CONNECTED',
  })
  const { updateShipStationConfigurationMutation } = useUpdateShipStationConfigurationMutation()
  const { deleteShipStationConfigurationMutation } = useDeleteShipStationConfigurationMutation()
  const { pushToThirdPartyMutation } = usePushToThirdPartyMutation()
  const tagManager = useTagManager()
  const appConnectEvent = useAppConnectEvent('ship_station')

  const getStatus = (): AppStatus => {
    if (creatorIntegrationsQuery.data?.shipstation?.status === 'NOT_CONNECTED' && stores?.length)
      return 'NOT_CONFIGURED'

    if (shipStationConfigurationQuery.data?.enabled && shipStationConfigurationQuery.data?.broken) {
      return 'BROKEN'
    }

    return creatorIntegrationsQuery.data?.shipstation?.status
  }

  const displaySettingsSubmitSuccessToast = () => {
    Toasts.notify('ShipStation settings saved successfully.', { type: 'success' })
  }

  const handleSyncFulfillments = () => {
    pushToThirdPartyMutation.mutate([], {
      onSuccess: () => {
        displaySettingsSubmitSuccessToast()
      },
    })
  }

  const handleSettingsSubmitSuccess = () => {
    appConnectEvent.trigger()

    if (!shipStationConfigurationQuery?.data?.storeId) {
      open(SyncFulfillmentsModal, {
        onConfirm: (syncType) => {
          if (syncType === 'SYNC_ALL') {
            handleSyncFulfillments()
          } else {
            displaySettingsSubmitSuccessToast()
          }
        },
      })
    } else {
      displaySettingsSubmitSuccessToast()
    }

    invalidateCreatorIntegrationsQuery()
    invalidateShipStationConfigurationQuery()
    invalidateCreatorIntegrationsV2Query()
    tagManager.trigger('app_connected', { app_name: 'ship_station' })
  }

  const updateSettings = (values: SettingsFormValues) => {
    updateShipStationConfigurationMutation.mutate(
      [
        {
          apiKey: values.apiKey,
          apiSecret: isInitialApiSecret(values.apiSecret) ? undefined : values.apiSecret,
          storeId: Number(values.storeId),
        },
      ],
      {
        onSuccess: handleSettingsSubmitSuccess,
      },
    )
  }

  const handleFetchStores = (values: SettingsFormValues) => {
    setStoresValues({ apiKey: values.apiKey, apiSecret: values.apiSecret })
  }

  const handleSettingsSubmit = (values: SettingsFormValues) => {
    if (
      shipStationConfigurationQuery?.data?.storeId &&
      values.storeId !== shipStationConfigurationQuery?.data?.storeId.toString() &&
      unfulfilledCountQuery.data?.count
    ) {
      open(ConfirmStoreDisconnectModal, {
        type: 'CHANGE_SHOP',
        pendingFulfillmentsCount: unfulfilledCountQuery.data?.count,
        onConfirm: () => {
          updateSettings(values)
        },
      })

      return
    }

    updateSettings(values)
  }

  const handleDisconnect = () => {
    deleteShipStationConfigurationMutation.mutate([], {
      onSuccess: async () => {
        await Promise.all([
          invalidateCreatorIntegrationsQuery(),
          invalidateShipStationConfigurationQuery(),
          invalidateCreatorIntegrationsV2Query(),
          invalidateStores(),
        ])
        setStoresValues(undefined)
      },
    })
  }

  const storesOptions = useMemo(() => {
    return (
      stores?.map((store) => ({
        value: store.storeId.toString(),
        label: store.storeName,
      })) || []
    )
  }, [stores])

  return {
    shipStation: {
      settings: {
        isLoading: isLoadingStores,
        isSubmitting: updateShipStationConfigurationMutation.isLoading,
        initialValues: {
          apiKey: storeValues?.apiKey || shipStationConfigurationQuery.data?.apiKey || '',
          apiSecret: storeValues?.apiSecret || shipStationConfigurationQuery.data?.apiSecret || '',
          storeId: shipStationConfigurationQuery.data?.storeId?.toString() || '',
        },
        stores: storesOptions,
        storesErrorCode,
        onFetchStores: handleFetchStores,
        onSubmit: handleSettingsSubmit,
      },
      data: shipStationConfigurationQuery.data,
      pendingFulfillmentsCount: unfulfilledCountQuery.data?.count,
      status: getStatus(),
      isLoading:
        creatorIntegrationsQuery.isLoading || shipStationConfigurationQuery.isInitialLoading,
      onDisconnect: handleDisconnect,
    },
  }
}
